import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { IOrderRegistration, Page, ResponseError } from '../type/type';
import { NavigateFunction } from 'react-router-dom';
import { IProduct } from './useProduct';
import { useFranchiseStore } from './useFranchise';

// 주문 타입
interface IOrder {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  status: IContractStatus;
  email: string;
  custInfoNames: string;
  telNo: string;
  confirmYn: 'Y' | 'N';
  readyToSendYn: 'Y' | 'N';
  realtimeYn: 'Y' | 'N';
  sendDt: string | null;
  sendMsg: string | null;
  franchiseId: string;
  resultFile: ResultFile;
  confirmDt: string | null;
  openAiBatch: IOpenAiBatch;
}

//배치 상태
interface IOpenAiBatch {
  inputFileId: string;
  batchId: string;
  status: any;
  lastCheckedAt: string;
  completedAt: string;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  applyYn: string;
}

// 주문 목록 테이블 타입
export interface IOrderTable extends IOrder {
  productCode: string;
  productName: string;
}

// 프롬프트 프로세스 타입
export interface IContractPromptProcess {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  status: IContractPromptStatus;
  failCount: number;
  contractId: string;
  promptId: string;
  promptTitle: string;
  oder: number;
  resultContent: string | null;
  validationData: string | null;
  model: string | null;
  promptTokens: number;
  completionTokens: number;
}

interface ResultFile {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  originalFileName: string;
  storedFileName: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  fileFullUrl: string;
}

interface IContractCustInfo {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  name: string;
  solarType: 'SOLAR' | 'LUNAR';
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  sexType: 'M' | 'F';
}

// 주문 상태 타입
interface IContractStatus {
  code: string;
  description: string;
  onleUser: boolean;
}

// 주문 프롬프트 상태 타입
interface IContractPromptStatus {
  code: string;
  description: string;
}

// 주문 확정 모달  데이터 타입
interface IContractModal {
  companyName: string;
  companyChargeAmount: number;
  contractAmount: number;
  realTimeSurAmount: number;
}

// 주문 목록 응답 타입
interface OrderResponse extends Page {
  content: ({
    product: IProduct;
  } & IOrder)[];
}

// 주문 상세 응답 타입
interface OrderDetailResponse {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  email: string;
  telNo: string;
  confirmYn: 'Y' | 'N';
  readyToSendYn: 'Y' | 'N';
  sendYn: 'Y' | 'N';
  realtimeYn: 'Y' | 'N';
  sendDt: string;
  product: IProduct;
  contractPromptProcesses: IContractPromptProcess[];
  contractCustInfo: IContractCustInfo[];
}

// 주문 상태 조회 응답 타입
type ContractStatusResponse = IContractStatus[];

// 주문 프롬프트 상태 조회 응답 타입
type ContractPromptStatusResponse = IContractPromptStatus[];

// 주문 등록 요청 타입
type PostOrderRequest = {};

// 주문 업데이트 요청 타입
export type UpdateOrderRequest = {
  realtimeYn?: 'Y' | 'N';
  email?: string;
  telNo?: string;
  contractCustInfo1?: {
    name?: string;
    solarType?: 'SOLAR' | 'LUNAR' | 'LUNAR_INTER';
    year?: string;
    month?: string;
    day?: string;
    hour?: string;
    minute?: string;
    sexType?: 'M' | 'F';
  };
  contractCustInfo2?: {
    name?: string;
    solarType?: 'SOLAR' | 'LUNAR' | 'LUNAR_INTER';
    year?: string;
    month?: string;
    day?: string;
    hour?: string;
    minute?: string;
    sexType?: 'M' | 'F';
  };
};

// Zustand Store Data 타입
type OrderData = {
  // 로딩
  getOrderLoading: boolean; // 주문목록 로딩
  postOrderLoading: boolean; // 주문등록 로딩
  deleteOrderLoading: boolean; // 주문삭제 로딩
  updateOrderLoading: boolean; // 주문수정 로딩
  confirmOrderLoading: boolean; // 주문확정 로딩

  updatePromptProcessLoading: boolean; // 주문확정 상세 프롬프트 수정 로딩
  retryContractPromptLoading: boolean; // 주문확정 상세 프롬프트 전체 재시도 로딩
  makePDFMySelfLoading: boolean; // 주문확정 상세 PDF 수동 제작 로딩
  sendPDFLoading: boolean; // 주문확정 상품 전송 로딩

  // 주문 목록
  orders: IOrderTable[];
  ordersPage: Page | null;

  // 주문 상태
  contractStatus: IContractStatus[];

  // 주문 프롬프트 상태
  contractPromptStatus: IContractPromptStatus[];

  // 주문 상세
  orderDetail: OrderDetailResponse | null;

  // 주문확정 모달 데이터
  contractModalData: IContractModal | null;
};

// Zustand Store Action 타입
type OrderAction = {
  // 주문 목록 가져오기
  getOrders: (params: {
    realtimeYn?: 'Y' | 'N';
    telNo?: string;
    email?: string;
    custName?: string;
    confirmYn?: 'Y' | 'N' | '';
    statusList?: string;
    page?: number;
    size?: number;
    contractStartDt?: string;
    contractEndDt?: string;
    confirmStartDt?: string;
    confirmEndDt?: string;
    franchiseId?: string;
  }) => void;

  // 주문 상태 가져오기
  getContractStatus: () => void;

  // 주문 프롬프트 상태 가져오기
  getContractPromptStatus: () => void;

  // 주문 상세 가져오기
  getOrderDetail: (id: string) => void;

  // 주문 확정
  confirmOrders: (ids: string[], navigate?: NavigateFunction) => Promise<void>;

  // 주문 확정 체크
  checkConfirm: (ids: string[]) => Promise<void>;

  // 주문 확정 체크 초기화
  resetCheckConfirm: () => void;

  // 주문 등록
  postOrder: (
    orderData: IOrderRegistration[],
    navigate?: NavigateFunction
  ) => Promise<void>;

  // 주문 업데이트
  updateOrder: (id: string, orderData: UpdateOrderRequest) => void;

  // 주문 삭제
  deleteOrder: (id: string, navigate: NavigateFunction) => void;

  deleteOrders: (ids: string[]) => void;

  // 주문 확정 프롬프트 결과 수정
  updatePromptProcess: (id: string, resultContent: string) => void;

  // 프롬프트 리트라이
  retryContractPrompt: (idList: string[]) => void;

  // PDF 수동 제작
  makePDFMySelf: (idList: string[]) => void;

  // 상품 전송
  sendPDF: (idList: string[]) => void;

  //제작중 상품 프롬포트 일괄 재시도
  promptRetry: (idList: string[]) => void;
};

export const useOrderStore = create<OrderData & OrderAction>((set, get) => ({
  // 로딩
  getOrderLoading: false,
  postOrderLoading: false,
  deleteOrderLoading: false,
  updateOrderLoading: false,
  confirmOrderLoading: false,

  updatePromptProcessLoading: false,
  retryContractPromptLoading: false,
  makePDFMySelfLoading: false,
  sendPDFLoading: false,

  // 주문 목록
  orders: [],
  ordersPage: null,

  // 주문 상태
  contractStatus: [],

  // 주문 프롬프트 상태
  contractPromptStatus: [],

  // 주문 상세
  orderDetail: null,

  // 주문 확정 모달 데이터
  contractModalData: null,

  // 주문 목록 가져오기
  getOrders: async ({
    telNo,
    email,
    custName,
    confirmYn,
    realtimeYn,
    statusList,
    page,
    size,
    contractStartDt,
    contractEndDt,
    confirmStartDt,
    confirmEndDt,
    franchiseId,
  }) => {
    set({ getOrderLoading: true }); // 주문 목록 로딩 시작
    set({ orderDetail: null }); // 주문 상세 초기화

    try {
      const response = await apiClient.get<OrderResponse>('/contract', {
        params: {
          telNo,
          email,
          'contractCustInfo.name': custName,
          confirmYn,
          realtimeYn,
          statusList,
          page,
          size,
          contractStartDt,
          contractEndDt,
          confirmStartDt,
          confirmEndDt,
          franchiseId,
        },
      });

      const { content, ...rest } = response.data;
      const orders = content.map(order => {
        const { product, ...rest } = order;
        return {
          ...rest,
          productCode: product.productCode,
          productName: product.productName,
        };
      });

      set({ orders }); // 주문 목록 세팅
      set({ ordersPage: rest }); // 주문 페이지 세팅
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('주문 대기 목록을 가져오지 못했습니다.');
    }

    set({ getOrderLoading: false }); // 주문 목록 로딩 끝
  },

  // 주문 상태 가져오기
  getContractStatus: async () => {
    const contractStatus = get().contractStatus;

    // 데이터가 있으면 호출 X
    if (contractStatus.length > 0) return;

    try {
      const response = await apiClient.get<ContractStatusResponse>(
        '/contract/status'
      );
      set({ contractStatus: response.data });
    } catch (error) {
      console.log(error);
    }
  },

  // 주문 프롬프트 상태 가져오기
  getContractPromptStatus: async () => {
    const contractPromptStatus = get().contractPromptStatus;

    // 데이터가 있으면 호출 X
    if (contractPromptStatus.length > 0) return;

    try {
      const response = await apiClient.get<ContractPromptStatusResponse>(
        '/contract-prompt-process/status'
      );
      set({ contractPromptStatus: response.data });
    } catch (error) {
      console.log(error);
    }
  },

  // 주문 상세 가져오기
  getOrderDetail: async id => {
    try {
      const response = await apiClient.get<OrderDetailResponse>(
        `/contract/${id}`
      );
      set({ orderDetail: response.data });
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('주문 상세를 가져오지 못했습니다.');
    }
  },

  // 주문 확정
  confirmOrders: async (ids, navigate) => {
    set({ orders: [] }); // 주문 목록 초기화
    set({ confirmOrderLoading: true }); // 주문확정 로딩 시작

    try {
      const response = await apiClient.post('/contract/confirm', {
        idList: ids,
      });
      if (response.status === 200) {
        toast.success('주문이 성공적으로 확정되었습니다!');
        if (navigate) {
          navigate('/order/list');
        } else {
          await get().getOrders({ confirmYn: 'N' });

          const franchiseStore = useFranchiseStore.getState();
          await franchiseStore.getMyFranchise();
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('주문 확정 실패:', error);
        toast.error('주문 확정에 실패했습니다.');
      }
      await get().getOrders({ confirmYn: 'N' });
    }

    set({ confirmOrderLoading: false }); // 주문확정 로딩 시작
  },

  // 주문 확정 체크 모달
  checkConfirm: async ids => {
    try {
      const response = await apiClient.post('/contract/check-confirm', {
        idList: ids,
      });
      if (response.status === 200) {
        set({ contractModalData: response.data });
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('check-confirm 실패:', error);
        toast.error('복비 데이터를 가져오지 못했습니다.');
      }
      throw error; // Promise reject를 위해 에러를 다시 throw
    }
  },

  // 주문 확정 체크 모달 초기화
  resetCheckConfirm: () => {
    set({ contractModalData: null });
  },

  // 주문 등록
  postOrder: async (orderData, navigate) => {
    set({ orders: [] }); // 주문 목록 초기화
    set({ postOrderLoading: true }); // 주문등록 로딩 시작

    try {
      const response = await apiClient.post('/contract', orderData);
      if (response.status === 200) {
        // DB 저장 완료를 위해 3초 대기
        await new Promise(resolve => setTimeout(resolve, 3000));
        toast.success('주문이 성공적으로 등록되었습니다!');
        
        if (navigate) {
          navigate('/order/list');
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('주문 등록 실패:', error);
        toast.error('주문 등록에 실패했습니다.');
      }
    }

    set({ postOrderLoading: false }); // 주문등록 로딩 끝
  },

  // 주문 업데이트
  updateOrder: async (id, orderData) => {
    set({ orders: [] }); // 주문 목록 초기화
    set({ updateOrderLoading: true }); // 주문수정 로딩 시작

    try {
      const response = await apiClient.patch(`/contract/${id}`, orderData);
      if (response.status === 200) {
        toast.success('주문 상세가 성공적으로 수정되었습니다!');
        await get().getOrderDetail(id);
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('주문 상세 수정 실패:', error);
        toast.error('주문 상세 수정에 실패했습니다.');
      }
    }

    set({ updateOrderLoading: false }); // 주문수정 로딩 끝
  },

  // 주문 삭제
  deleteOrder: async (id, navigate) => {
    set({ orders: [] }); // 주문 목록 초기화
    set({ deleteOrderLoading: true }); // 주문삭제 로딩 시작

    try {
      const response = await apiClient.delete(`/contract/${id}`);
      if (response.status === 200) {
        toast.success('주문 상세가 성공적으로 삭제되었습니다!');
        navigate('/order/list');
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('주문 상세 삭제 실패:', error);
        toast.error('주문 상세 삭제에 실패했습니다.');
      }
    }

    set({ deleteOrderLoading: false }); // 주문삭제 로딩 끝
  },

  deleteOrders: async (ids) => {
    set({ orders: [] }); // 주문 목록 초기화
    set({ deleteOrderLoading: true }); // 주문삭제 로딩 시작

    try {
      const response = await apiClient.delete(`/contract`, {
        data: {
          idList: ids
        }
      });
      if (response.status === 200) {
        toast.success('선택한 주문이 성공적으로 삭제되었습니다!');
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('주문 삭제 실패:', error);
        toast.error('주문 삭제에 실패했습니다.');
      }
    }

    set({ deleteOrderLoading: false }); // 주문삭제 로딩 끝
  },

  // 주문 확정 프롬프트 결과 수정
  updatePromptProcess: async (id, resultContent) => {
    set({ updatePromptProcessLoading: true }); // 주문확정 상세 프롬프트 수정 로딩 시작
    try {
      const response = await apiClient.patch(`/contract-prompt-process/${id}`, {
        resultContent,
      });
      if (response.status === 200) {
        toast.success('프롬프트 결과가 성공적으로 수정되었습니다!');
        const detailId = get().orderDetail?.id;
        if (detailId) {
          await get().getOrderDetail(detailId);
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('프롬프트 결과 수정 실패:', error);
        toast.error('프롬프트 결과 수정에 실패했습니다.');
      }
    }
    set({ updatePromptProcessLoading: false }); // 주문확정 상세 프롬프트 수정 로딩 시작
  },

  // 프롬프트 리트라이
  retryContractPrompt: async idList => {
    set({ retryContractPromptLoading: true }); // 주문확정 상세 프롬프트 전체 재시도 로딩 시작
    try {
      const response = await apiClient.post('/contract-prompt-process/retry', {
        idList,
      });
      if (response.status === 200) {
        toast.success('프롬프트 재시도가 완료되었습니다!');
        const detailID = get().orderDetail?.id;
        if (detailID) {
          await get().getOrderDetail(detailID);
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('프롬프트 재시도 실패:', error);
        toast.error('프롬프트 재시도가 실패했습니다.');
      }
    }
    set({ retryContractPromptLoading: false }); // 주문확정 상세 프롬프트 전체 재시도 로딩 끝
  },

  // PDF 수동 제작
  makePDFMySelf: async idList => {
    set({ makePDFMySelfLoading: true }); // 주문확정 상세 PDF 수동 제작 로딩 시작
    try {
      const response = await apiClient.post('/contract/make-content', {
        idList,
      });
      if (response.status === 200) {
        toast.success('PDF 수동 제작이 완료되었습니다!');
        const detailID = get().orderDetail?.id;
        if (detailID) {
          await get().getOrderDetail(detailID);
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('PDF 수동 제작 실패:', error);
        toast.error('PDF 수동 제작이 실패했습니다.');
      }
    }
    set({ makePDFMySelfLoading: false }); // 주문확정 상세 PDF 수동 제작 로딩 끝
  },

  // 상품 전송
  sendPDF: async idList => {
    set({ sendPDFLoading: true }); // 주문확정 상품 전송 로딩 시작
    try {
      const response = await apiClient.post('/contract/send', {
        idList,
      });
      if (response.status === 200) {
        toast.success('상품 전송이 완료되었습니다.');
        await get().getOrders({ confirmYn: 'Y' });
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 전송 실패:', error);
        toast.error('상품 전송이 실패했습니다.');
      }
    }

    set({ sendPDFLoading: false }); // 주문확정 상품 전송 로딩 끝
  },

  // 상품 전송
  promptRetry: async idList => {
    set({ sendPDFLoading: true }); // 주문확정 상품 전송 로딩 시작
    try {
      const response = await apiClient.post(`/contract-prompt-process/retry/all?contractIds=${idList}`);
      if (response.status === 200) {
        toast.success('상품 제작이 재시도되었습니다.');
        await get().getOrders({ confirmYn: 'Y' });
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 전송 실패:', error);
        toast.error('상품 전송이 실패했습니다.');
      }
    }

    set({ sendPDFLoading: false }); // 주문확정 상품 전송 로딩 끝
  },
}));
